/**
 * Apple Wallet & Google Wallet Integration
 *
 * Generates digital wallet passes for SECRET orders with QR codes.
 * Supports pass updates and expiration.
 */

import { supabase } from './supabase'

export interface WalletPassData {
  orderId: string
  orderNumber: string
  eventTitle: string
  eventLocation: string
  eventDate: string
  qrCodeData: string
  customerName: string
  totalAmount: number
}

/**
 * Check if Apple Wallet is available
 */
export function isAppleWalletAvailable(): boolean {
  // Apple Wallet is available on iOS Safari
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  return isIOS && isSafari
}

/**
 * Check if Google Wallet is available
 */
export function isGoogleWalletAvailable(): boolean {
  // Google Wallet is available on Android
  const isAndroid = /Android/.test(navigator.userAgent)

  return isAndroid
}

/**
 * Generate Apple Wallet pass URL
 */
export async function generateAppleWalletPass(passData: WalletPassData): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No active session')
    }

    const response = await fetch('/.netlify/functions/generate-wallet-pass', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        type: 'apple',
        ...passData,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to generate Apple Wallet pass')
    }

    const data = await response.json()
    return data.passUrl
  } catch (error) {
    console.error('Apple Wallet pass generation failed:', error)
    throw error
  }
}

/**
 * Generate Google Wallet pass URL
 */
export async function generateGoogleWalletPass(passData: WalletPassData): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No active session')
    }

    const response = await fetch('/.netlify/functions/generate-wallet-pass', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        type: 'google',
        ...passData,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to generate Google Wallet pass')
    }

    const data = await response.json()
    return data.passUrl
  } catch (error) {
    console.error('Google Wallet pass generation failed:', error)
    throw error
  }
}

/**
 * Add pass to Apple Wallet
 */
export async function addToAppleWallet(passData: WalletPassData): Promise<void> {
  const passUrl = await generateAppleWalletPass(passData)

  // Open the pass URL which will trigger "Add to Wallet"
  window.location.href = passUrl
}

/**
 * Add pass to Google Wallet
 */
export async function addToGoogleWallet(passData: WalletPassData): Promise<void> {
  const passUrl = await generateGoogleWalletPass(passData)

  // Open Google Wallet with the pass
  window.open(passUrl, '_blank')
}

/**
 * Update existing wallet pass
 */
export async function updateWalletPass(
  orderId: string,
  updates: Partial<WalletPassData>
): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No active session')
    }

    await fetch('/.netlify/functions/update-wallet-pass', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        orderId,
        updates,
      }),
    })
  } catch (error) {
    console.error('Failed to update wallet pass:', error)
    throw error
  }
}

/**
 * Get wallet pass download link
 */
export function getWalletPassDownloadLink(orderId: string, type: 'apple' | 'google'): string {
  return `/.netlify/functions/download-wallet-pass?orderId=${orderId}&type=${type}`
}
